import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { TableComponent } from "../../components/Table";
import {
  createUser,
  deactivateUser,
  getUsers,
  updateUser,
  UserData,
} from "../../api/users";
import { useNotification } from "../../context/notification.context";

type FormState = {
  username: string;
  password: string;
  nombre: string;
  mail: string;
  rol: string;
  porcentaje: string;
  telefono: string;
};

const emptyForm: FormState = {
  username: "",
  password: "",
  nombre: "",
  mail: "",
  rol: "Gestor",
  porcentaje: "0",
  telefono: "",
};

const roleOptions = ["Superadministrador", "Administrador", "Gestor", "Tesorero"];

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const { getError, getSuccess } = useNotification();

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      getError(`Error al cargar usuarios: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setOpenForm(true);
  };

  const openEdit = (user: UserData) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      password: "",
      nombre: user.nombre,
      mail: user.mail || "",
      rol: user.rol,
      porcentaje: String(user.porcentaje ?? 0),
      telefono: user.telefono || "",
    });
    setOpenForm(true);
  };

  const closeForm = () => {
    setOpenForm(false);
    setEditingUser(null);
    setForm(emptyForm);
  };

  const validateForm = () => {
    if (!form.username.trim()) return "El usuario es obligatorio";
    if (!form.nombre.trim()) return "El nombre es obligatorio";
    if (!editingUser && form.password.trim().length < 4) {
      return "La contraseña debe tener al menos 4 caracteres";
    }
    if (editingUser && form.password.trim() && form.password.trim().length < 4) {
      return "La contraseña debe tener al menos 4 caracteres";
    }
    if (form.porcentaje !== "" && Number.isNaN(Number(form.porcentaje))) return "La comisión debe ser numérica";
    return null;
  };

  const submitForm = async () => {
    const validationError = validateForm();
    if (validationError) {
      getError(validationError);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        username: form.username.trim(),
        nombre: form.nombre.trim(),
        mail: form.mail.trim(),
        rol: form.rol,
        porcentaje: Number(form.porcentaje) || 0,
        telefono: form.telefono.trim() || undefined,
        ...(form.password.trim() ? { password: form.password.trim() } : {}),
      };

      if (editingUser) {
        await updateUser(editingUser.id, payload);
        getSuccess("Usuario actualizado correctamente");
      } else {
        await createUser({ ...payload, password: form.password.trim() });
        getSuccess("Usuario creado correctamente");
      }

      closeForm();
      await loadUsers();
    } catch (error) {
      getError(`Error al guardar usuario: ${error}`);
      setLoading(false);
    }
  };

  const runDelete = async () => {
    if (confirmDeleteId === null) return;
    try {
      setLoading(true);
      await deactivateUser(confirmDeleteId);
      getSuccess("Usuario eliminado correctamente");
      setConfirmDeleteId(null);
      await loadUsers();
    } catch (error) {
      getError(`Error al eliminar usuario: ${error}`);
      setLoading(false);
    }
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "acciones",
        headerName: "Acciones",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => openEdit(params.row as UserData)}
              title="Editar"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmDeleteId(params.row.id)}
              title="Eliminar"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      { field: "nombre", headerName: "Nombre y Apellido", flex: 1, minWidth: 160, disableColumnMenu: true },
      { field: "username", headerName: "Nombre de usuario", flex: 1, minWidth: 140, disableColumnMenu: true },
      { field: "mail", headerName: "Correo electrónico", flex: 1.5, minWidth: 180, disableColumnMenu: true },
      { field: "rol", headerName: "Rol", flex: 1, minWidth: 150, disableColumnMenu: true },
      {
        field: "porcentaje",
        headerName: "Porcentaje de comisión",
        flex: 1,
        minWidth: 160,
        disableColumnMenu: true,
        renderCell: (params) => {
          const val = params.row.porcentaje;
          const num = val === null || val === undefined || val === "" ? 0 : Number(val);
          return <span>{isNaN(num) ? 0 : num}%</span>;
        },
      },
    ],
    []
  );

  return (
    <Container maxWidth="xl" sx={{ pt: 5 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        <IconButton aria-label="routeback" size="small" component={Link} to="/">
          <ArrowBackIos fontSize="small" />
        </IconButton>
        Gestión de Usuarios
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nuevo Usuario
        </Button>
      </Box>

      <TableComponent
        columnData={columns}
        rowData={users}
        actions={false}
        exportTitle="Listado de Usuarios"
        loading={loading}
      />

      {/* Create / Edit dialog */}
      <Dialog open={openForm} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 600, px: 4, pt: 3, pb: 1 }}>
          {editingUser ? "Editar usuario" : "Agregar usuario"}
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: 2, pb: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre y Apellido"
                value={form.nombre}
                onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                fullWidth
                placeholder="Ingrese nombre y apellido"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Correo electrónico"
                value={form.mail}
                onChange={(e) => setForm((prev) => ({ ...prev, mail: e.target.value }))}
                fullWidth
                placeholder="Ingrese el correo"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre de usuario"
                value={form.username}
                onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                fullWidth
                placeholder="Ingrese el nombre de usuario"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={editingUser ? "Nueva contraseña (opcional)" : "Contraseña"}
                type="password"
                value={form.password}
                onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                fullWidth
                placeholder="Ingrese la contraseña"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Rol"
                value={form.rol}
                onChange={(e) => setForm((prev) => ({ ...prev, rol: e.target.value }))}
                fullWidth
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Porcentaje de comisión"
                value={form.porcentaje}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                    setForm((prev) => ({ ...prev, porcentaje: val }));
                  }
                }}
                fullWidth
                placeholder="Ej: 2.5"
                inputProps={{ inputMode: "decimal" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                value={form.telefono}
                onChange={(e) => setForm((prev) => ({ ...prev, telefono: e.target.value }))}
                fullWidth
                placeholder="Ingrese el teléfono"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 0 }}>
          <Button
            onClick={closeForm}
            fullWidth
            sx={{
              borderRadius: 0,
              py: 2,
              fontSize: "1rem",
              bgcolor: "#e53935",
              color: "#fff",
              "&:hover": { bgcolor: "#c62828" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={submitForm}
            fullWidth
            sx={{
              borderRadius: 0,
              py: 2,
              fontSize: "1rem",
              bgcolor: "#43a047",
              color: "#fff",
              "&:hover": { bgcolor: "#2e7d32" },
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Eliminar Usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Confirma que desea eliminar este usuario? Esta acción lo marcará como inactivo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancelar</Button>
          <Button onClick={runDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
